import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { CircleDashed } from 'lucide-react'
import { useState } from 'react'

import { actionQuery, type ActionDef } from '@/api/actions'

import { categoryLabel, describeAction, type RoleSummary, type StepSummary } from './describe'
import { DetailFrame, DetailLoading, DetailMissing, SummarySection } from './DetailFrame'
import { DocEditor } from './editor/DocEditor'
import { consumeJustCreated } from './editor/NewDocPage'
import { TYPE_ICON } from './parts'
import { PreviewPanel } from './PreviewPanel'

/**
 * `/actions/$id` — one action: try it with puppets (preview), then read it in plain words — who
 * takes part (and what kind of thing each one is), what must be true first, what happens step by
 * step, what is true afterwards, and what happens instead when it can't. The JSON sits folded
 * away under Advanced. `?edit=1` opens the manual editor beside the preview; every save plays the
 * saved version.
 */

export function ActionDetail({ id, editing = false, validateDelay }: { id: string; editing?: boolean; validateDelay?: number }) {
  const q = useQuery(actionQuery(id))
  const navigate = useNavigate()
  const [saves, setSaves] = useState(0)
  // Made a moment ago on the "New action" page: play it straight away.
  const [fresh] = useState(() => consumeJustCreated('action', id))
  if (q.isPending) return <DetailLoading what="action" />
  if (q.isError || !q.data) return <DetailMissing kind="action" error={q.error} />
  const action = q.data
  const preview = (
    <div className="card flex flex-col gap-3 p-5">
      <h2 className="text-lg font-semibold text-ink">Try it</h2>
      {editing ? <p className="text-xs text-ink-3">Plays the saved version — save to try your changes.</p> : null}
      <PreviewPanel action={action} replayKey={saves} autoPlay={fresh} />
    </div>
  )
  return (
    <DetailFrame kind="action" doc={action} title={action.label} tag={action.category ? categoryLabel(action.category) : null} onReload={() => void q.refetch()} editing={editing}>
      {editing ? (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
          <DocEditor
            key={`${action.id}:${action.rev}:${action.source ?? ''}`}
            kind="action"
            mode="edit"
            doc={action}
            validateDelay={validateDelay}
            onSaved={() => setSaves((n) => n + 1)}
            onReload={() => void q.refetch()}
            onClose={() => void navigate({ to: '/actions/$id', params: { id: action.id }, search: { kind: 'action' } })}
          />
          <aside className="flex min-w-0 flex-col gap-4 xl:sticky xl:top-20 xl:self-start">{preview}</aside>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          {preview}
          <ActionSummaryView action={action} />
        </div>
      )}
    </DetailFrame>
  )
}

export function ActionSummaryView({ action }: { action: ActionDef }) {
  const sum = describeAction(action)
  return (
    <div className="card flex flex-col gap-5 p-5" data-testid="action-summary">
      <h2 className="text-lg font-semibold text-ink">How it works</h2>
      {action.idea_template ? (
        <p className="rounded-xl bg-paper-2 px-3 py-2 text-sm text-ink-2">
          In a story: <span className="text-ink">{fillTemplate(action.idea_template)}</span>
        </p>
      ) : null}
      <SummarySection title="Who takes part" testId="summary-roles">
        <RoleList roles={sum.roles} />
      </SummarySection>
      <SummarySection title="What must be true first" testId="summary-needs">
        {sum.needs.length ? <Bullets items={sum.needs} /> : <p className="text-sm text-ink-2">Nothing special.</p>}
      </SummarySection>
      <SummarySection title="Step by step" testId="summary-steps">
        <StepList steps={sum.steps} />
      </SummarySection>
      {sum.after.length ? (
        <SummarySection title="Afterwards" testId="summary-after">
          <Bullets items={sum.after} />
        </SummarySection>
      ) : null}
      {sum.fallbacks.length ? (
        <SummarySection title="If it can’t happen" testId="summary-fallbacks">
          <Bullets items={sum.fallbacks} />
        </SummarySection>
      ) : null}
      {sum.effects.length || sum.duration ? (
        <SummarySection title="Extras">
          <Bullets items={[...(sum.duration ? [sum.duration] : []), ...sum.effects.map((e) => `Effect: ${e}`)]} />
        </SummarySection>
      ) : null}
    </div>
  )
}

/** `{actor} throws the {object} to {partner}` → "the actor throws the object to the partner". */
function fillTemplate(t: string): string {
  return t.replace(/\{([a-z][a-z0-9_]*)\}/g, (_m, role: string) => role.replace(/_/g, ' '))
}

export function RoleList({ roles }: { roles: RoleSummary[] }) {
  if (roles.length === 0) return <p className="text-sm text-ink-2">Nobody in particular.</p>
  return (
    <ul className="flex flex-col gap-2">
      {roles.map((r) => (
        <li key={r.role} className="flex items-start gap-2 text-sm" data-testid="summary-role" data-role={r.role}>
          <span className="mt-0.5 inline-flex shrink-0 gap-0.5 text-ink-3" aria-hidden>
            {r.types.length === 0 ? (
              <CircleDashed className="size-4" />
            ) : (
              r.types.map((t) => {
                const Icon = TYPE_ICON[t]
                return <Icon key={t} className="size-4" />
              })
            )}
          </span>
          <span>
            <span className="font-medium text-ink">{r.title}</span> <span className="text-ink-2">— {r.sentence}</span>
          </span>
        </li>
      ))}
    </ul>
  )
}

export function StepList({ steps }: { steps: StepSummary[] }) {
  if (steps.length === 0) return <p className="text-sm text-ink-2">No steps yet.</p>
  return (
    <ol className="flex flex-col gap-3">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3 text-sm" data-testid="summary-step">
          <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-ink" aria-hidden>
            {i + 1}
          </span>
          <div className="min-w-0">
            <p className="font-medium text-ink">
              {s.title}
              {s.length ? <span className="font-normal text-ink-3"> · {s.length}</span> : null}
            </p>
            <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-ink-2">
              {s.lines.map((l, j) => (
                <li key={`${j}:${l}`}>{l}</li>
              ))}
            </ul>
          </div>
        </li>
      ))}
    </ol>
  )
}

export function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="list-disc space-y-0.5 pl-5 text-sm text-ink-2">
      {items.map((t, i) => (
        <li key={`${i}:${t}`}>{t}</li>
      ))}
    </ul>
  )
}
